import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw, AlertTriangle, CheckCircle2, XCircle, SkipForward,
  Coins, HardDrive, Layers, Info,
} from "lucide-react";
import { apiUtils } from "../../../utils/apiUtils";

/**
 * Scraping & Media - operational view for video-bearing platforms.
 *
 * Answers the questions video introduces and nothing else in the system shows:
 * what are we spending, how long until credits run out, is it working, what is
 * escalating, and where is storage.
 */

const STATUS_META = {
  ok: { label: "OK", icon: CheckCircle2, cls: "text-emerald-600" },
  failed: { label: "Failed", icon: XCircle, cls: "text-red-600" },
  skipped: { label: "Skipped", icon: SkipForward, cls: "text-muted-foreground" },
  pending: { label: "Pending", icon: RefreshCw, cls: "text-amber-600" },
};

const TIER_LABEL = {
  1: "1 · triage (free)",
  2: "2 · transcript",
  3: "3 · frames",
  4: "4 · comments",
};

const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
};

export default function ScrapingMediaManagement() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, st, j] = await Promise.all([
        apiUtils.get("/api/media-ops/status").catch(() => null),
        apiUtils.get("/api/media-ops/stats?days=7").catch(() => null),
        apiUtils.get("/api/media-ops/jobs?limit=25").catch(() => null),
      ]);
      setStatus(s?.data?.data || null);
      setStats(st?.data?.data || null);
      setJobs(j?.data?.data || []);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? "You need global admin access to view media operations."
          : "Could not load media operations."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Credit exhaustion is an OUTAGE: at zero, TikTok scanning stops silently.
  const creditsLow = status?.credits != null && status.credits < 100;
  const daysLow = status?.daysRemaining != null && status.daysRemaining < 7;

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      {/* Loud, because running out means scanning stops with no other signal. */}
      {(creditsLow || daysLow) && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Credits running low.</strong>{" "}
            {status.credits} remaining
            {status.daysRemaining != null && <> - about <strong>{status.daysRemaining} days</strong> at the current rate</>}.
            At zero, TikTok scanning stops silently.
          </span>
        </div>
      )}

      {status && !status.vendorConfigured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          Data-acquisition vendor is not configured - TikTok scans will be skipped rather than failed.
        </div>
      )}

      {status && !status.reachable && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Scraper unreachable - credit balance and storage figures are unavailable.
          {status.error && <span className="opacity-70"> ({status.error})</span>}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Scraping &amp; Media</h2>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* --- headline numbers ------------------------------------------------ */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          icon={Coins}
          label="Credits remaining"
          value={status?.credits ?? "-"}
          tone={creditsLow ? "text-red-600" : "text-foreground"}
        />
        <Tile
          icon={Coins}
          label="Days at current rate"
          value={status?.daysRemaining ?? "-"}
          sub={status?.creditsPerDay ? `${status.creditsPerDay}/day` : null}
          tone={daysLow ? "text-red-600" : "text-foreground"}
        />
        <Tile
          icon={Layers}
          label="Escalation rate"
          value={stats ? `${stats.escalationRate}%` : "-"}
          sub="tier 3+ - high means tiering stopped saving"
          tone={stats && stats.escalationRate > 50 ? "text-amber-600" : "text-foreground"}
        />
        <Tile
          icon={HardDrive}
          label="Frame storage"
          value={status?.storage ? fmtBytes(status.storage.bytes) : "-"}
          sub={status?.storage ? `${status.storage.objects} objects · ${status.storage.driver}` : null}
        />
      </div>

      {/* --- outcomes + failure shape ---------------------------------------- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={`Outcomes · last ${stats?.days ?? 7} days`}>
          {!stats?.total ? (
            <Empty>No video jobs yet. Rows appear once a TikTok scan runs.</Empty>
          ) : (
            <div className="space-y-2">
              {Object.entries(STATUS_META).map(([k, m]) => {
                const n = stats.byStatus?.[k] || 0;
                if (!n) return null;
                const Icon = m.icon;
                return (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className={`inline-flex items-center gap-1.5 ${m.cls}`}>
                      <Icon className="h-4 w-4" /> {m.label}
                    </span>
                    <span className="font-medium text-foreground">
                      {n} <span className="text-muted-foreground">({Math.round((n / stats.total) * 100)}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel title="Why jobs failed">
          {!stats?.failureReasons?.length ? (
            <Empty>No failures.</Empty>
          ) : (
            <ul className="space-y-2 text-sm">
              {stats.failureReasons.map((f) => (
                <li key={f.reason} className="flex items-center justify-between gap-3">
                  <code className="truncate rounded bg-muted px-1.5 py-0.5 text-[13px] text-foreground">
                    {f.reason}
                  </code>
                  <span className="shrink-0 font-medium text-foreground">{f.count}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            One reason dominating usually means the vendor changed, not that the videos are odd.
          </p>
        </Panel>
      </div>

      {/* --- escalation funnel ------------------------------------------------ */}
      <Panel title="Escalation funnel">
        {!stats?.tiers?.length ? (
          <Empty>No data yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Reason</th>
                  <th className="py-2 pr-4 text-right">Videos</th>
                  <th className="py-2 text-right">Credits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.tiers.map((t, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-4 text-foreground">{TIER_LABEL[t.tier] || t.tier}</td>
                    <td className="py-2 pr-4">
                      <code className="rounded bg-muted px-1.5 py-0.5 text-[13px] text-foreground">{t.reason}</code>
                    </td>
                    <td className="py-2 pr-4 text-right text-foreground">{t.count}</td>
                    <td className="py-2 text-right text-muted-foreground">{t.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* --- per-video drill-down --------------------------------------------- */}
      <Panel title="Recent videos">
        {!jobs.length ? (
          <Empty>No video jobs recorded.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4 text-right">Frames</th>
                  <th className="py-2 text-right">Credits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.map((j) => {
                  const m = STATUS_META[j.status] || STATUS_META.pending;
                  return (
                    <tr key={j._id}>
                      <td className="py-2 pr-4">
                        <div className="text-foreground">@{j.accountHandle}</div>
                        <div className="text-xs text-muted-foreground">{j.platform}</div>
                      </td>
                      <td className={`py-2 pr-4 ${m.cls}`}>
                        {m.label}
                        {j.failureReason && (
                          <div className="text-xs text-muted-foreground">{j.failureReason}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-foreground">{j.tierReached}</td>
                      <td className="py-2 pr-4 text-right text-foreground">{j.framesExtracted}</td>
                      <td className="py-2 text-right text-muted-foreground">{j.creditsCharged}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, tone = "text-foreground" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card">
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}
